import { TeamEffectivenessV2ReportData, V2AssessmentWithScoring } from "@/store/admin/adminTypes";
import { TRAIT_CLASS_RANGES, Trait, TRAITS, FOUR_GROUP_TRAIT_GROUPINGS } from "@/lib/consts";
import { CompetencyStatus } from "@/lib/competencies";
import { CANDIDATE_PALETTE, getInitials, getShortName } from "@/components/project-reports/evaluation-report/shared";

export { CANDIDATE_PALETTE, getInitials, getShortName };

// ---------------------------------------------------------------------------
// Priority levels (Critical / Important / Supporting)
// ---------------------------------------------------------------------------

export type PriorityLevel = 0 | 1 | 2;
export type PriorityKey = "critical" | "important" | "supporting";

export const PRIORITY_KEYS: Record<PriorityLevel, PriorityKey> = { 2: "critical", 1: "important", 0: "supporting" };
export const PRIORITY_LEVELS: Record<PriorityKey, PriorityLevel> = { critical: 2, important: 1, supporting: 0 };
export const PRIORITY_ORDER: PriorityKey[] = ["critical", "important", "supporting"];

// ---------------------------------------------------------------------------
// Team % thresholds and colours (mockup: ≥80 teal, 61–79 blue, 50–60 amber, <50 coral)
// ---------------------------------------------------------------------------

export const PCT_STRENGTH = 80;
export const PCT_GAP = 50;

// Per-competency "wide spread" thresholds. Spread is the sample standard deviation (n − 1) of the
// visible leaders' scores; it is wide when at or above the 67th percentile of within-team SDs
// observed across 40 historical System-3 teams (source: "Thresholds for Competency Spread Size"
// spreadsheet, APPROACH tab, large_spread column, Aug 27 2026).
export const SPREAD_WIDE_THRESHOLDS: Record<Trait, number> = {
  adaptability: 10.52,
  auth: 18.24,
  con: 13.02,
  decisive: 19.21,
  empathy: 10.92,
  goal: 10.64,
  grit: 15.53,
  integrity: 19.44,
  intellect: 20,
  mentor: 12.95,
  persuade: 12.5,
  relationship: 11.58,
  strategic: 15.21,
  tact: 10.81,
  vision: 17.89,
};

/** Sample standard deviation (n − 1), matching the R `sd()` used to build the spread norms. 0 for fewer than 2 values. */
export function sampleStdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const sumSq = values.reduce((acc, v) => acc + (v - mean) ** 2, 0);
  return Math.sqrt(sumSq / (values.length - 1));
}

export function isWideSpread(trait: Trait, spread: number): boolean {
  return spread >= SPREAD_WIDE_THRESHOLDS[trait];
}

export type PctBand = "high" | "good" | "mixed" | "low";

export const PCT_BAND_COLORS: Record<PctBand, string> = {
  high: "#1D9E75",
  good: "#185FA5",
  mixed: "#EF9F27",
  low: "#D85A30",
};

export function getPctBand(pct: number): PctBand {
  if (pct >= 80) return "high";
  if (pct >= 61) return "good";
  if (pct >= 50) return "mixed";
  return "low";
}

export function getPctColor(pct: number): string {
  return PCT_BAND_COLORS[getPctBand(pct)];
}

// Team-level status for a competency, derived from % of the team prioritizing it.
// Feeds getOrientationText() the same way a candidate's `{trait}_status` does.
export function getTeamStatusLevel(pct: number): 0 | 1 | 2 {
  return pct >= PCT_STRENGTH ? 2 : pct >= PCT_GAP ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Leaders and subgroups
// ---------------------------------------------------------------------------

export interface LeaderSummary {
  id: string;
  name: string;
  initials: string;
  shortName: string;
  label: string; // anonymised label, e.g. "L1" / short id
  color: string;
  role: string;
  tags: string[];
  readiness: string;
  traitScores: Record<string, number>;
  traitStatus: Record<string, CompetencyStatus>;
  prioritizedCount: number;
  assessment: V2AssessmentWithScoring;
}

export interface Subgroup {
  id: string; // tag
  label: string;
  color: string;
  memberIds: string[];
}

export const DEFAULT_SUBGROUP_COLOUR = "#5c5e64";

export function getTraitStatus(trait: string, score: number | undefined): CompetencyStatus {
  const range = TRAIT_CLASS_RANGES[trait as Trait];
  if (score === undefined || score === null || Number.isNaN(score) || !range) return "neutral";
  if (score >= range.high) return "highly-prioritized";
  if (score >= range.low) return "prioritized";
  return "neutral";
}

export function isPrioritized(trait: string, score: number | undefined): boolean {
  return getTraitStatus(trait, score) !== "neutral";
}

export function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

// Builds the per-leader view model shared by all tabs. Leaders are sorted by name and the
// palette is assigned by that order so colours are stable across tabs.
export function buildLeaderSummaries(reportData: TeamEffectivenessV2ReportData): LeaderSummary[] {
  const assessments = sortByName((reportData.assessments ?? []).map((a) => ({ name: a.candidate.name, a }))).map((x) => x.a);

  return assessments.map((assessment, i) => {
    const traitScores = ((assessment.scoring?.trait_scores as Record<string, number>) ?? {}) as Record<string, number>;
    const traitStatus: Record<string, CompetencyStatus> = {};
    TRAITS.forEach((t) => (traitStatus[t] = getTraitStatus(t, traitScores[t])));
    const prioritizedCount = TRAITS.filter((t) => traitStatus[t] !== "neutral").length;

    return {
      id: assessment.identifier,
      name: assessment.candidate.name,
      initials: getInitials(assessment.candidate.name),
      shortName: getShortName(assessment.candidate.name),
      label: assessment.identifier.substring(0, 4).toUpperCase(),
      color: CANDIDATE_PALETTE[i % CANDIDATE_PALETTE.length],
      role: reportData.candidateRoles?.[assessment.identifier] ?? "",
      tags: (reportData.tags?.[assessment.identifier] ?? []).slice().sort(),
      readiness: (assessment.scoring?.readiness as string) ?? "",
      traitScores,
      traitStatus,
      prioritizedCount,
      assessment,
    };
  });
}

// Subgroups are the tags assigned to participants in the wizard.
export function buildSubgroups(reportData: TeamEffectivenessV2ReportData, leaders: LeaderSummary[]): Subgroup[] {
  const tags = Array.from(new Set(Object.values(reportData.tags ?? {}).flat())).sort();
  return tags.map((tag) => ({
    id: tag,
    label: tag,
    color: reportData.tagColours?.[tag] ?? DEFAULT_SUBGROUP_COLOUR,
    memberIds: leaders.filter((l) => l.tags.includes(tag)).map((l) => l.id),
  }));
}

// ---------------------------------------------------------------------------
// Team-level competency stats
// ---------------------------------------------------------------------------

export interface CompetencyStat {
  trait: Trait;
  name: string;
  level: PriorityLevel;
  priorityKey: PriorityKey;
  orientationId: string;
  pct: number; // % of visible leaders prioritizing (prioritized or highly prioritized)
  prioritizedCount: number;
  total: number;
  avg: number; // mean score of visible leaders
  spread: number; // sample standard deviation of visible leaders' scores, rounded
  wideSpread: boolean; // spread at/above this competency's SPREAD_WIDE_THRESHOLDS value
  band: PctBand;
  color: string;
  teamStatus: 0 | 1 | 2;
}

export function getOrientationForTrait(trait: string): string {
  return Object.keys(FOUR_GROUP_TRAIT_GROUPINGS).find((id) => FOUR_GROUP_TRAIT_GROUPINGS[id].includes(trait as Trait)) ?? "";
}

export function pctFor(trait: string, leaders: LeaderSummary[]): number {
  if (!leaders.length) return 0;
  return Math.round((leaders.filter((l) => isPrioritized(trait, l.traitScores[trait])).length / leaders.length) * 100);
}

export function avgFor(trait: string, leaders: LeaderSummary[]): number {
  const scores = leaders.map((l) => l.traitScores[trait]).filter((s) => typeof s === "number" && !Number.isNaN(s));
  if (!scores.length) return 0;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function buildCompetencyStats(leaders: LeaderSummary[], traitPriorities: { [trait: string]: 0 | 1 | 2 }, nameOf: (trait: string) => string): CompetencyStat[] {
  return TRAITS.map((trait) => {
    const scores = leaders.map((l) => l.traitScores[trait]).filter((s) => typeof s === "number" && !Number.isNaN(s));
    const prioritizedCount = leaders.filter((l) => isPrioritized(trait, l.traitScores[trait])).length;
    const pct = leaders.length ? Math.round((prioritizedCount / leaders.length) * 100) : 0;
    const level = (traitPriorities?.[trait] ?? 0) as PriorityLevel;
    const stdDev = sampleStdDev(scores);
    return {
      trait,
      name: nameOf(trait),
      level,
      priorityKey: PRIORITY_KEYS[level],
      orientationId: getOrientationForTrait(trait),
      pct,
      prioritizedCount,
      total: leaders.length,
      avg: scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
      spread: Math.round(stdDev),
      wideSpread: isWideSpread(trait, stdDev),
      band: getPctBand(pct),
      color: getPctColor(pct),
      teamStatus: getTeamStatusLevel(pct),
    };
  });
}

// ---------------------------------------------------------------------------
// Summary classification (Strengths / Gaps / Areas to watch)
// ---------------------------------------------------------------------------

export type WatchKind = "under" | "over" | "split";

export interface SummaryClassification {
  strengths: CompetencyStat[];
  gaps: CompetencyStat[];
  watchUnder: CompetencyStat[];
  watchOver: CompetencyStat[];
  watchSplit: CompetencyStat[];
}

export function classifyCompetencies(stats: CompetencyStat[]): SummaryClassification {
  const out: SummaryClassification = { strengths: [], gaps: [], watchUnder: [], watchOver: [], watchSplit: [] };
  stats.forEach((c) => {
    if (c.level === 2 && c.pct >= PCT_STRENGTH) {
      out.strengths.push(c);
      return;
    }
    if (c.level === 2 && c.pct < PCT_GAP) {
      out.gaps.push(c);
      return;
    }
    if (c.level === 1 && c.pct < PCT_GAP) out.watchUnder.push(c);
    if (c.level === 0 && c.pct >= PCT_STRENGTH) out.watchOver.push(c);
    if (c.wideSpread) out.watchSplit.push(c);
  });

  // Highest first by default; the two "too few prioritize it" lists read lowest first so the biggest shortfall leads.
  const byPct = (dir: 1 | -1) => (a: CompetencyStat, b: CompetencyStat) => (a.pct - b.pct) * dir;
  out.strengths.sort(byPct(-1));
  out.gaps.sort(byPct(1));
  out.watchUnder.sort(byPct(1));
  out.watchOver.sort(byPct(-1));
  out.watchSplit.sort((a, b) => b.spread - a.spread);
  return out;
}

/** Joins names: 1→bare, 2→"A and B", 3+→"A, B, and C" */
export function joinNames(names: string[]): string {
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

// ---------------------------------------------------------------------------
// Shared filter state (Team Profile + By Leader tabs)
// ---------------------------------------------------------------------------

export interface ReportFilters {
  activeLeaderIds: string[];
  activeSubgroupIds: string[];
  showSubgroups: boolean;
  hideNames: boolean;
}

export function filterLeaders(leaders: LeaderSummary[], subgroups: Subgroup[], filters: ReportFilters): LeaderSummary[] {
  return leaders.filter((l) => {
    if (!filters.activeLeaderIds.includes(l.id)) return false;
    // Leaders without any subgroup are only hidden when every subgroup is deselected.
    if (subgroups.length === 0) return true;
    if (l.tags.length === 0) return filters.activeSubgroupIds.length > 0;
    return l.tags.some((t) => filters.activeSubgroupIds.includes(t));
  });
}

export function displayName(leader: LeaderSummary, hideNames: boolean): string {
  return hideNames ? leader.label : leader.name;
}
